<template>
	<div class="create-circuit">
		<v-card class="create-circuit-card rounded-lg">
			<div class="create-circuit-header">
				<h1>Creación de circuito</h1>
			</div>
			<v-form
				ref="createCircuitForm"
				v-model="formValid"
				class="create-circuit-form"
			>
				<!-- Name of the circuit -->
				<v-text-field
					label="Nombre del circuito"
					v-model="name"
					:rules="[rules.notEmpty]"
				></v-text-field>

				<v-textarea
					label="Descripción del circuito"
					v-model="description"
				></v-textarea>

				<!-- Poster file upload -->
				<v-file-input
					@change="fileUploaded"
					accept="image/png, image/jpeg, image/jpg"
					cleareable
					ref="fileInput"
					:rules="validFile"
					label="Cartel del circuito"
				></v-file-input>

				<div class="card-row">
					<!-- Range date selector -->
					<v-dialog
						ref="startDateDialog"
						v-model="startDateModal"
						:return-value.sync="startDate"
						persistent
						width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="startDate | formatDate"
								label="Fecha de inicio"
								prepend-icon="mdi-calendar"
								readonly
								v-bind="attrs"
								v-on="on"
								:rules="[rules.notEmpty, correctDates]"
							></v-text-field>
						</template>
						<v-date-picker
							v-model="startDate"
							scrollable
							locale="es-ES"
							first-day-of-week="1"
							reactive
							@change="$refs.startDateDialog.save(startDate)"
						>
							<v-spacer></v-spacer>
							<v-btn
								text
								color="primary"
								@click="startDateModal = false"
								>Cancel</v-btn
							>
						</v-date-picker>
					</v-dialog>

					<v-dialog
						ref="startTimeDialog"
						v-model="startTimeModal"
						:return-value.sync="startTime"
						persistent
						width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="startTime"
								label="Hora de inicio"
								prepend-icon="mdi-calendar"
								readonly
								v-bind="attrs"
								v-on="on"
								:rules="[rules.notEmpty, correctDates]"
							></v-text-field>
						</template>
						<v-time-picker
							v-model="startTime"
							scrollable
							reactive
							format="24hr"
							@change="$refs.startTimeDialog.save(startTime)"
						>
							<v-spacer></v-spacer>
							<v-btn
								text
								color="primary"
								@click="startTimeModal = false"
								>Cancel</v-btn
							>
						</v-time-picker>
					</v-dialog>
				</div>

				<div class="card-row">
					<!-- Range date selector -->
					<v-dialog
						ref="finishDateDialog"
						v-model="finishDateModal"
						:return-value.sync="finishDate"
						persistent
						width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="finishDate | formatDate"
								label="Fecha de fin"
								prepend-icon="mdi-calendar"
								readonly
								v-bind="attrs"
								v-on="on"
								:rules="[rules.notEmpty, correctDates]"
							></v-text-field>
						</template>
						<v-date-picker
							v-model="finishDate"
							scrollable
							locale="es-ES"
							first-day-of-week="1"
							reactive
							:allowed-dates="allowedDates"
							@change="$refs.finishDateDialog.save(finishDate)"
						>
							<v-spacer></v-spacer>
							<v-btn
								text
								color="primary"
								@click="finishDateModal = false"
								>Cancel</v-btn
							>
						</v-date-picker>
					</v-dialog>

					<v-dialog
						ref="finishTimeDialog"
						v-model="finishTimeModal"
						:return-value.sync="finishTime"
						persistent
						width="290px"
					>
						<template v-slot:activator="{ on, attrs }">
							<v-text-field
								:value="finishTime"
								label="Hora de fin"
								prepend-icon="mdi-calendar"
								readonly
								v-bind="attrs"
								v-on="on"
								:rules="[rules.notEmpty, correctDates]"
							></v-text-field>
						</template>
						<v-time-picker
							v-model="finishTime"
							scrollable
							reactive
							format="24hr"
							@change="$refs.finishTimeDialog.save(finishTime)"
						>
							<v-spacer></v-spacer>
							<v-btn
								text
								color="primary"
								@click="finishTimeModal = false"
								>Cancel</v-btn
							>
						</v-time-picker>
					</v-dialog>
				</div>

				<v-text-field
					prepend-icon="mdi-currency-eur"
					label="Precio del circuito"
					v-model="price"
					lang="es"
					type="number"
					:rules="[rules.notEmpty, rules.positive]"
				>
				</v-text-field>

				<v-combobox
					prepend-icon="mdi-tag-multiple"
					v-model="tags"
					label="Etiquetas circuito"
					multiple
					clearable
					chips
					:items="existingTags"
					:loading="tagsLoading"
					:search-input.sync="searchInput"
					@change="searchInput = ''"
				></v-combobox>

				<!-- ACTION BUTTONS -->
				<v-card-actions class="card-actions">
					<v-spacer></v-spacer>
					<!-- Button to cancel adding the circuit -->
					<v-btn
						text
						@click="cancel"
						color="error"
						elevation="0"
						class="rounded-lg"
						:disabled="loading"
						>Cancelar</v-btn
					>

					<!-- Button to save the circuit -->

					<v-btn
						@click="submit"
						color="success"
						:loading="loading"
						:disabled="loading || !formValid"
						elevation="0"
						class="rounded-lg"
						>Crear circuito</v-btn
					>
				</v-card-actions>
			</v-form>
		</v-card>
	</div>
</template>

<script>
	import axios from "axios";
	import moment from "moment";

	export default {
		name: "AdminCreateRoute",
		data() {
			return {
				//Component data
				loading: false,

				//Form data
				name: "",
				description: "",

				startDateModal: false,
				startDate: moment(new Date()).format("YYYY-MM-DD"),
				startTimeModal: false,
				startTime: moment(new Date()).format("HH:mm"),

				finishDateModal: false,
				finishDate: moment(new Date()).add("1", "day").format("YYYY-MM-DD"),
				finishTimeModal: false,
				finishTime: moment(new Date().setHours(0, 0, 0, 0)).format("HH:mm"),

				races: [],

				tags: [],
				existingTags: [],
				tagsLoading: false,
				searchInput: "",

				file: "ejemplo",
				modalDateRange: false,
				price: 0,
				formValid: false,

				//Form validation rules
				rules: {
					notEmpty: (v) => v !== "" || "El campo no puede estar vacío",
					positive: (v) => v >= 0 || "El precio no puede ser negativo",
				},
				validFile: [
					(file) =>
						file !== undefined ||
						"El cartel del circuito no puede estar vacíao",
					(file) => {
						if (file) {
							return (
								file.size < 5 * 1024 * 1024 ||
								"El archivo tiene que pesar menos de 5MB"
							);
						}
						return true;
					},
				],
				correctDates: () => {
					if (
						this &&
						this.startDate &&
						this.startTime &&
						this.finishDate &&
						this.finishTime
					) {
						return (
							moment(
								`${this.startDate} ${this.startTime}`,
								"YYYY-MM-DD HH:mm"
							).valueOf() <
								moment(
									`${this.finishDate} ${this.finishTime}`,
									"YYYY-MM-DD HH:mm"
								).valueOf() ||
							"La fecha y hora de final debe ser posterior a la de inicio"
						);
					} else {
						return true;
					}
				},
			};
		},
		filters: {
			formatDate(date) {
				if (date === "") {
					return "";
				} else {
					return moment(date).locale("es-ES").format("ll");
				}
			},
		},
		watch: {
			startDate() {
				this.$refs.createCircuitForm.validate();
			},
			finishDate() {
				this.$refs.createCircuitForm.validate();
			},
			startTime() {
				this.$refs.createCircuitForm.validate();
			},
			finishTime() {
				this.$refs.createCircuitForm.validate();
			},
		},
		computed: {
			//Show date ranges in the text field
			dateRangeText() {
				if (this.startFinishDate[0] === "") {
					return "";
				} else {
					return this.startFinishDate.join(" -> ");
				}
			},
		},

		methods: {
			/**
			 * Set allowed dates
			 */
			allowedDates(date) {
				date = new Date(date).setHours(0, 0, 0, 0);

				//If the date is greater or equal to today, it is allowed
				return date >= moment(this.startDate).valueOf();
			},

			/**
			 * When a file is uploaded, convert it to JS object of coordinates
			 */
			async fileUploaded(e) {
				try {
					if (e) {
						this.file = e;
					} else {
						e = undefined;
					}
				} catch (error) {
					this.$emit("snackbar-event", {
						color: "error",
						text: "No se ha podido procesar la imagen",
					});
					e = undefined;
				}
			},

			/**
			 * When the user submits the form
			 */
			async submit() {
				try {
					this.$emit("loading", true);
					this.loading = true;
					this.$refs.createCircuitForm.validate();

					if (this.formValid) {
						const formData = new FormData();
						formData.append("circuitPoster", this.file);
						formData.set("name", this.name);
						formData.set("description", this.description);
						formData.set(
							"startDate",
							moment(`${this.startDate}`, "YYYY-MM-DD HH:mm").format()
						);
						formData.set(
							"finishDate",
							moment(
								`${this.finishDate}`,
								"YYYY-MM-DD HH:mm"
							).format()
						);
						formData.set("price", this.price);
						formData.set("tags", JSON.stringify(this.tags));
						formData.set("races", JSON.stringify(this.races));

						await axios.post("admin/circuit", formData, {
							headers: {
								"content-type": "multipart/form-data",
							},
						});

						//Then show the snackbar
						this.$emit("snackbar-event", {
							color: "success",
							text: "circuito creado con éxito",
						});

						//Go back to the main AdminCircuitList
						this.$router.push({ name: "AdminCircuitList" });
					} else {
						this.loading = false;
						//Show error snackbar
						this.$emit("snackbar-event", {
							color: "error",
							text: "Compruebe que todos los campos son correctos",
						});
					}
					//Hide loading
				} catch (error) {
					if (error.response.data) {
						if (error.response.status === 401) {
							this.$router.push({ name: "AdminLogin" });
						}
						this.$emit("snackbar-event", {
							color: "error",
							text: error.response.data,
						});
					} else {
						//Show error snackbar
						this.$emit("snackbar-event", {
							color: "error",
							text:
								"Ha ocurrido un error, vuelva a intentarlo más tarde",
						});
					}
				} finally {
					//Hide loading
					this.$emit("loading", false);
					this.loading = false;
				}
			},

			/**
			 * Returns to main page if admin cancels
			 */
			cancel() {
				this.$router.push({ name: "AdminCircuitList" });
			},

			/**
			 * Fetches existing tags
			 */
			async fetchTags() {
				try {
					this.tagsLoading = true;
					const response = await axios.get("/races/tags");
					this.existingTags = response.data;
				} catch (error) {
					//No tags is not a problem
					this.existingTags = [];
				} finally {
					this.tagsLoading = false;
				}
			},
		},
		async mounted() {
			this.fetchTags();

			try {
				await axios.post("/is-admin");
			} catch (error) {
				this.$router.push({ name: "AdminLogin" });
			}
		},
	};
</script>

<style lang="scss" scoped>
	.map {
		height: 75vh;
		width: 100%;
		z-index: 0;
	}

	@import "../../scss/_mixins.scss";
	@import "../../scss/_variables.scss";
	.create-circuit {
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;

		background-color: #f0f0f3;
		padding: 2em;

		.create-circuit-card {
			width: 100%;
			max-width: 1400px;
			height: fit-content;

			.create-circuit-form {
				padding: 2em;
			}

			.create-circuit-header {
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				margin: 0;
				padding: 2em;
				background: #292931;

				h1 {
					flex: 1;
					padding: 0 0.5em;
					color: #fff;
					font-size: 2.5em;
				}
			}

			.price-field {
				width: fit-content;
			}

			.card-row {
				display: grid;
				grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
				column-gap: 1em;
			}

			.card-actions {
				margin-top: 2em;
				padding: 0;
			}
		}
	}
</style>

<style lang="scss">
	.leaflet-div-icon:first-of-type {
		background-color: var(--v-success-base);
		border-radius: 50%;
		width: 16px !important;
		height: 16px !important;
		position: relative;
		top: -4px;
		left: -4px;
	}
</style>
